import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import ChatAgentService from '../../Services/ChatAgentService';

/**
 * Custom hook wrapping the chat-agent AgentCore Runtime client. Each
 * user turn is a fresh HTTPS invocation that streams ServerEvents back
 * over SSE; we reduce those events into a linear `blocks` list the widget
 * can render.
 *
 * A "block" is the unit the UI renders in order:
 *   { kind: 'text', role, content, streaming }
 *   { kind: 'tool', name, summary, status }
 *   { kind: 'file', filename, url, contentType, expiresAt }
 *   { kind: 'image', url, altText, caption }
 *   { kind: 'question', prompt, choices, answered }
 *   { kind: 'approval', tool, action, details, decision }
 *   { kind: 'usage', input, output, cacheRead, cacheWrite, estimatedCostUsd }
 *   { kind: 'map', points: [{lat, lng, label, color}] }
 *   { kind: 'proposal', proposal: {offer_id, page_key, rows: [...]} }
 *   { kind: 'commit_result', result: {offer_id, page_key, summary, results: [...]} }
 *
 * Keeping the reducer here (instead of inside the widget) lets the widget
 * stay a thin view + gives us a single place to test state transitions.
 */

const SESSION_ID_STORAGE_KEY = 'ph_chat_agent_session_id';
// Stored as `{sessionId, blocks}` so a refresh that finds a mismatched
// sessionId (shouldn't happen, but defensive) discards stale blocks rather
// than mixing them into a different conversation.
const BLOCKS_STORAGE_KEY = 'ph_chat_agent_blocks';
// AgentCore runtime requires >=33 chars. A uuid is 36, and the `chat-`
// prefix pushes us comfortably past the threshold while staying greppable
// in server logs.
const SESSION_ID_PREFIX = 'chat-';

/**
 * Where ONE chat's session id and blocks live.
 *
 * Both were fixed strings while there was one chat in the app — the floating
 * widget. A second mounted chat (the Knowledge Base screen's embedded Ask
 * tab) sharing them would not be a second conversation: both instances would
 * read the same sessionId, write over each other's block snapshots, and
 * `reset()` in one would wipe the other's history. So a chat is identified by
 * its `namespace`, and the keys are derived from it.
 *
 * The DEFAULT namespace keeps the two original key names to the letter. The
 * floating widget's conversation is in a live operator's localStorage right
 * now; renaming its keys would silently orphan it.
 */
const storageKeys = (namespace) => (namespace
  ? {
    sessionId: `${SESSION_ID_STORAGE_KEY}:${namespace}`,
    blocks: `${BLOCKS_STORAGE_KEY}:${namespace}`,
  }
  : { sessionId: SESSION_ID_STORAGE_KEY, blocks: BLOCKS_STORAGE_KEY });

/**
 * EVERY read and write here is wrapped.
 *
 * Safari in private mode, and any browser set to block all cookies, make
 * `localStorage` THROW on access rather than return null. The block snapshot
 * has always allowed for that; the session id did not — and it is read in a
 * `useState` initialiser, so the throw came out as a render error rather than
 * a lost conversation.
 *
 * That was harmless while the only chat was a button on the dashboard, which
 * nobody opens in a private window. It stopped being harmless when the chat
 * moved into a screen's FRAME: the dispatcher's board now mounts this hook on
 * every page load, so a dispatcher whose phone blocks storage would have got a
 * white screen instead of a board. The chat simply works in memory instead,
 * and a reload loses the thread — which is the right way for this to fail.
 */
const getStoredSessionId = (keys) => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(keys.sessionId);
  } catch {
    return null;
  }
};

const saveSessionId = (keys, sessionId) => {
  if (typeof window === 'undefined') return;
  try {
    if (sessionId) window.localStorage.setItem(keys.sessionId, sessionId);
    else window.localStorage.removeItem(keys.sessionId);
  } catch {
    // Storage unavailable: the id lives in React state for this page load,
    // which is all the runtime needs to keep one conversation going.
  }
};

const mintSessionId = () => {
  const uuid = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    // Fallback for very old browsers — extremely unlikely to hit in
    // practice, but keeps us robust.
    : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `${SESSION_ID_PREFIX}${uuid}`;
};

const ensureSessionId = (existing) => {
  if (existing && existing.length >= 33) return existing;
  return mintSessionId();
};

const loadBlocksForSession = (keys, sessionId) => {
  if (typeof window === 'undefined' || !sessionId) return [];
  try {
    const raw = window.localStorage.getItem(keys.blocks);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.sessionId !== sessionId || !Array.isArray(parsed.blocks)) return [];
    // Drop any mid-stream flags from the snapshot — the stream that set
    // them was killed by the page reload, so the blocks are final.
    return parsed.blocks.map((b) => (b && b.streaming ? { ...b, streaming: false } : b));
  } catch {
    return [];
  }
};

const saveBlocks = (keys, sessionId, blocks) => {
  if (typeof window === 'undefined' || !sessionId) return;
  try {
    window.localStorage.setItem(
      keys.blocks,
      JSON.stringify({ sessionId, blocks }),
    );
  } catch {
    // localStorage full / disabled — silent. The chat keeps working in
    // memory; refreshes just lose history.
  }
};

const clearStoredBlocks = (keys) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(keys.blocks);
  } catch {
    // Nothing was stored, so there is nothing to clear.
  }
};

// Client-side fallback parser for the agent's structured booking
// proposal block. Mirrors the server-side parser in
// `tools/_offer_ingestion_utils.parse_bookings_proposal` so the
// IngestWorkflow grid still renders when the chat-agent Lambda
// hasn't been redeployed yet (the typed `proposal` SSE event ships
// with the new main.py). When both fire, last-block-wins downstream
// keeps the backend event authoritative.
const PROPOSAL_FENCE_REGEX = /```json\s+bookings-proposal\s*\n([\s\S]*?)\n```/g;
function parseProposalFromText(text) {
  if (!text || typeof text !== 'string') return null;
  // Last block wins — matches the server-side semantics (revise flow
  // re-emits a fresh block).
  let last = null;
  let match = PROPOSAL_FENCE_REGEX.exec(text);
  while (match !== null) {
    [, last] = match;
    match = PROPOSAL_FENCE_REGEX.exec(text);
  }
  // Reset the global regex's lastIndex so the next caller starts clean.
  PROPOSAL_FENCE_REGEX.lastIndex = 0;
  if (!last) return null;
  try {
    const parsed = JSON.parse(last.trim());
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {object}  [options]
 * @param {string}  [options.storageNamespace] Which chat this is. Omit for the
 *   floating widget (the original, unnamespaced keys). Any other mounted chat
 *   MUST pass one, or the two will share a single conversation.
 * @param {function} [options.getAuthToken] Where the bearer token comes from,
 *   possibly async. Omit on the dashboard (its own session token); the driver
 *   portal passes its own, because the two surfaces keep their tokens in
 *   different places on purpose. Forwarded verbatim to ChatAgentService, which
 *   holds the reasoning.
 *
 *   Read once, at mount. A chat whose scope changes (e.g. the operator picks a
 *   different knowledge base) should be remounted with a new React `key`
 *   rather than handed a new namespace — the session id is minted in a
 *   `useState` initialiser and would otherwise outlive the scope it belongs to.
 */
/**
 * Every `estimatedCostUsd` the agent emitted this session, summed. Blocks with
 * no number (the server has no pricing for the model id) contribute nothing.
 * Lives here rather than in a widget so both chats tally it the same way.
 */
export function sumEstimatedCostUsd(blocks) {
  return (blocks || []).reduce((sum, block) => {
    if (!block || block.kind !== 'usage') return sum;
    return typeof block.estimatedCostUsd === 'number' ? sum + block.estimatedCostUsd : sum;
  }, 0);
}

export default function useChatAgent({ storageNamespace, getAuthToken } = {}) {
  // A useState initialiser, not useMemo: it runs exactly once, which is the
  // guarantee wanted here. Nothing ever calls the setter.
  const [keys] = useState(() => storageKeys(storageNamespace));

  const [sessionId, setSessionId] = useState(() => {
    const id = ensureSessionId(getStoredSessionId(keys));
    saveSessionId(keys, id);
    return id;
  });
  const [blocks, setBlocks] = useState(() => loadBlocksForSession(keys, sessionId));

  // Persist blocks to localStorage whenever they change so a refresh
  // restores the visible conversation. Server-side AgentCore Memory
  // keeps the agent's context for the same sessionId regardless, so
  // continuing a turn after refresh works correctly.
  useEffect(() => {
    saveBlocks(keys, sessionId, blocks);
  }, [keys, sessionId, blocks]);
  // The widget surfaces `status === 'connected'` as its ready state; we
  // keep the same vocabulary so ChatWidget doesn't need to change. The
  // service internally uses 'idle'|'streaming'|'error'; we translate.
  const [status, setStatus] = useState('connected');
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  /**
   * The last turn ended WITHOUT the server saying it was finished: the HTTP
   * body closed mid-answer, or the reader stopped it. What is on screen is a
   * fragment, and no further event will ever arrive to complete it.
   *
   * This is the ordinary outcome of a chat on a phone — SSE over mobile data
   * drops at a tunnel, a lift, a handover — so it is a first-class state
   * rather than an error. The surface says the answer stopped and offers to
   * ask again; `pending` is cleared either way, so the composer is never
   * left disabled waiting for a stream that has already gone.
   */
  const [interrupted, setInterrupted] = useState(false);

  const serviceRef = useRef(null);
  // Track the current streaming-assistant block id between text.delta events
  // so we can append deltas into one block rather than creating N blocks.
  const streamingIdRef = useRef(null);
  /**
   * Which turn is the live one. A superseded invocation (a second question
   * aborts the first) still settles its promise, and without this its
   * settlement would clear the NEW turn's `pending` and declare the new turn
   * interrupted — the race that turns one dropped answer into a chat that
   * looks broken for the rest of the shift.
   */
  const turnRef = useRef(0);
  /**
   * Did the server say this turn was finished (`turn.complete`, or an `error`
   * it reported itself)? Starts true so nothing is called interrupted before
   * a single question has been asked.
   */
  const completedRef = useRef(true);

  const handleServiceStatus = useCallback((next) => {
    if (next === 'error') setStatus('error');
    // 'idle' and 'streaming' both map to 'connected' from the widget's
    // POV — input readiness is gated on `pending`, not on `status`.
    else setStatus('connected');
  }, []);

  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'text.delta':
        setBlocks((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.kind === 'text' && last.role === 'assistant' && last.id === event.id && last.streaming) {
            const updated = { ...last, content: last.content + event.content };
            return [...prev.slice(0, -1), updated];
          }
          streamingIdRef.current = event.id;
          return [...prev, {
            id: event.id, kind: 'text', role: 'assistant', content: event.content, streaming: true,
          }];
        });
        break;

      case 'text.complete':
        setBlocks((prev) => {
          const updated = prev.map((b) => (
            b.kind === 'text' && b.id === event.id ? { ...b, streaming: false } : b
          ));
          // Client-side fallback: parse `bookings-proposal` fenced
          // blocks out of the now-complete assistant text and
          // synthesize a `proposal` block. Belt-and-suspenders so
          // the IngestWorkflow grid works even when the chat-agent
          // Lambda hasn't been redeployed with the typed SSE event
          // emission yet. Fallback id is deterministic per text
          // block so the same text never spawns two fallbacks; if
          // the backend ALSO emits a real proposal event later in
          // the same turn, both land and last-block-wins makes the
          // backend one authoritative.
          const completed = updated.find((b) => (
            b.kind === 'text' && b.id === event.id && b.role === 'assistant'
          ));
          if (completed) {
            const parsed = parseProposalFromText(completed.content);
            if (parsed) {
              const fallbackId = `${event.id}-proposal-fallback`;
              const already = updated.some((b) => (
                b.kind === 'proposal' && b.id === fallbackId
              ));
              if (!already) {
                return [...updated, {
                  id: fallbackId,
                  kind: 'proposal',
                  proposal: parsed,
                }];
              }
            }
          }
          return updated;
        });
        streamingIdRef.current = null;
        break;

      case 'tool.start':
        setBlocks((prev) => [...prev, {
          id: event.id, kind: 'tool', name: event.name, summary: event.summary, status: 'pending',
        }]);
        break;

      case 'tool.end':
        setBlocks((prev) => prev.map((b) => (
          b.kind === 'tool' && b.id === event.id
            ? { ...b, status: event.ok ? 'done' : 'error' }
            : b
        )));
        break;

      case 'file':
        setBlocks((prev) => [...prev, {
          id: `file-${event.filename}-${Date.now()}`,
          kind: 'file',
          filename: event.filename,
          url: event.url,
          contentType: event.contentType,
          expiresAt: event.expiresAt,
        }]);
        break;

      case 'image':
        setBlocks((prev) => [...prev, {
          id: `img-${Date.now()}`,
          kind: 'image',
          url: event.url,
          altText: event.altText,
          caption: event.caption,
        }]);
        break;

      case 'question':
        setBlocks((prev) => [...prev, {
          id: event.id,
          kind: 'question',
          prompt: event.prompt,
          choices: event.choices,
          answered: false,
        }]);
        break;

      case 'approval.request':
        // Write-action approval card. The agent called a write tool
        // with confirm=False (the only allowed shape for the first
        // call); the backend emits this so we can render an
        // Approve/Reject card. Click sends a canned chat message
        // back ("Yes — please proceed with confirm=true" or "No,
        // cancel") that the model is instructed to act on.
        setBlocks((prev) => [...prev, {
          id: event.id,
          kind: 'approval',
          tool: event.tool,
          action: event.action,
          details: event.details || {},
          decision: null,
        }]);
        break;

      case 'usage':
        // Per-turn token usage + estimated USD cost emitted by the
        // agent before turn.complete. Rendered as a small chip below
        // the assistant's reply. cacheRead is the prompt-cache win
        // (system prompt + tool defs reused at ~10× lower rate);
        // estimatedCostUsd is null when the model id isn't in the
        // pricing table on the server.
        setBlocks((prev) => [...prev, {
          id: `usage-${Date.now()}`,
          kind: 'usage',
          input: event.input || 0,
          output: event.output || 0,
          total: event.total || 0,
          cacheRead: event.cacheRead || 0,
          cacheWrite: event.cacheWrite || 0,
          estimatedCostUsd: event.estimatedCostUsd ?? null,
        }]);
        break;

      case 'overview':
        // A booking briefing from `brief_booking`, rendered VERBATIM.
        //
        // It does not arrive as assistant text and it is not parsed out of
        // one: the tool writes it to a channel the model never sees, so the
        // prose and — the part that matters — the code-authored `gaps` reach
        // the operator in the wording this codebase authored. A model asked
        // to relay a list of careful caveats shortens it, and the whole
        // point of the briefing is that it does not do that.
        setBlocks((prev) => [...prev, {
          id: event.id,
          kind: 'overview',
          bookingId: event.booking_id,
          overview: event.overview,
        }]);
        break;

      case 'map.preview':
        // One static map per turn summarising the geocode_address /
        // pricing_calculate(transfer) coordinates the model fed into
        // its tool calls. Operator-visible verification — if the pin
        // is in the wrong city they can call out the bad pick instead
        // of getting a quote based on hallucinated coordinates.
        setBlocks((prev) => [...prev, {
          id: event.id,
          kind: 'map',
          points: Array.isArray(event.points) ? event.points : [],
        }]);
        break;

      case 'proposal':
        // Structured booking proposal extracted server-side from the
        // assistant text's `\`\`\`json bookings-proposal` fenced block.
        // Routed to the IngestWorkflow page's <BookingProposalGrid>
        // (the ChatThread itself skips this block kind in the switch).
        // Last block wins downstream — the natural revise flow re-
        // emits a fresh block on the next turn.
        setBlocks((prev) => [...prev, {
          id: event.id,
          kind: 'proposal',
          proposal: event.proposal || {},
          // Per-row schema warnings the agent computed at proposal time
          // ({ rowIndex: [messages] }) — the grid flags them so the
          // operator can fix a bad field before "Ingest all". Absent on
          // the client-side fallback block (no typed event yet).
          fieldWarnings: event.field_warnings || null,
        }]);
        break;

      case 'commit_result':
        // Result of a `commit_bookings(confirm=true)` tool call:
        // per-row created/deduplicated/failed outcomes plus aggregate
        // summary counts. The grid binds to the latest commit_result
        // to render badges next to each row; ChatMessage skips this
        // block kind in its switch (falls through to default: null).
        setBlocks((prev) => [...prev, {
          id: event.id,
          kind: 'commit_result',
          result: event.result || {},
        }]);
        break;

      case 'turn.complete':
        completedRef.current = true;
        setPending(false);
        break;

      case 'error':
        // The SERVER said what went wrong, which is a finished turn with bad
        // news — not a stream that vanished. The difference matters: this one
        // has a message to show, so it must not also be dressed up as a
        // dropped connection.
        completedRef.current = true;
        setError(event.message);
        setPending(false);
        break;

      default:
        // Unknown event type — ignore. Forward-compatible with future
        // server events without requiring a UI release.
        break;
    }
  }, []);

  /**
   * The stream for `turn` is over, however it ended. Three things happen, and
   * all three are the point:
   *
   *   - `pending` is cleared. It used to be cleared ONLY by a `turn.complete`
   *     event, so a body that closed without one left the composer disabled
   *     for good and the chat unusable until a page reload. On a phone that
   *     is not an edge case.
   *   - any block still flagged `streaming` is closed AND marked
   *     `truncated`, so a fragment cannot sit there reading as the whole
   *     answer once the typing indicator stops.
   *   - if the server never said the turn was finished, the chat enters the
   *     `interrupted` state, which is what offers the question again.
   */
  const settleTurn = useCallback((turn) => {
    if (turn !== turnRef.current) return;
    const finished = completedRef.current;
    setPending(false);
    setBlocks((prev) => (prev.some((b) => b.streaming)
      // Same object back when there is nothing to close: this runs on every
      // turn, and a new array would re-write the localStorage snapshot for
      // no reason.
      ? prev.map((b) => (b.streaming ? { ...b, streaming: false, truncated: true } : b))
      : prev));
    if (!finished) setInterrupted(true);
  }, []);

  /** Opens a new turn: claims it, and clears the last one's verdict. */
  const openTurn = useCallback(() => {
    turnRef.current += 1;
    completedRef.current = false;
    setInterrupted(false);
    setPending(true);
    setError(null);
    return turnRef.current;
  }, []);

  // Instantiate the service once. AgentCore invocations are per-message,
  // so there's nothing to open/close on widget show/hide — just abort any
  // in-flight fetch on unmount.
  useEffect(() => {
    const service = new ChatAgentService({
      onEvent: handleEvent,
      onStatusChange: handleServiceStatus,
      getAuthToken,
    });
    serviceRef.current = service;
    return () => {
      service.cancel();
      if (serviceRef.current === service) serviceRef.current = null;
    };
  }, [handleEvent, handleServiceStatus, getAuthToken]);

  /**
   * Send one user turn.
   *
   * @param {string} text   What goes ON THE WIRE, verbatim.
   * @param {object} [opts]
   * @param {string} [opts.display] What the thread shows instead, when the two
   *   differ. A caller that scopes a conversation by prepending context to the
   *   prompt — the only way to scope it, since the wire protocol is fixed —
   *   would otherwise put its own plumbing in front of the operator. The
   *   agent reads `text`; the operator reads `display`.
   */
  const send = useCallback((text, opts = {}) => {
    const service = serviceRef.current;
    if (!service) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const shown = (opts.display ?? '').trim() || trimmed;
    // Optimistically render the user turn + a thinking state.
    setBlocks((prev) => [...prev, {
      id: `user-${Date.now()}`, kind: 'text', role: 'user', content: shown, streaming: false,
    }]);
    const turn = openTurn();
    service.sendMessage(trimmed, sessionId)
      // `ended` is the stream's body closing. A service that resolves
      // anything else (a cancellation, or a test double) is deliberately not
      // treated as the end of a turn.
      .then((outcome) => { if (outcome?.ended) settleTurn(turn); })
      .catch((err) => {
        setError(err.message);
        settleTurn(turn);
      });
  }, [sessionId, openTurn, settleTurn]);

  const answer = useCallback((questionId, value) => {
    const service = serviceRef.current;
    if (!service) return;
    setBlocks((prev) => prev.map((b) => (
      b.kind === 'question' && b.id === questionId ? { ...b, answered: true } : b
    )));
    const turn = openTurn();
    service.sendAnswer(questionId, value, sessionId)
      .then((outcome) => { if (outcome?.ended) settleTurn(turn); })
      .catch((err) => {
        setError(err.message);
        settleTurn(turn);
      });
  }, [sessionId, openTurn, settleTurn]);

  const decideApproval = useCallback((approvalId, decision) => {
    // Lock the buttons immediately so the operator can't double-click.
    setBlocks((prev) => prev.map((b) => (
      b.kind === 'approval' && b.id === approvalId ? { ...b, decision } : b
    )));

    // Send a canned, unambiguous response. The system prompt instructs
    // the model to recognise these phrases and either re-call the
    // tool with confirm=true (approve) or acknowledge cancellation
    // (reject) without calling anything else.
    const text = decision === 'approve'
      ? 'Yes — please proceed with the action above. Re-call the same tool with the same arguments plus confirm=true.'
      : 'No, cancel that action. Do not call the tool again.';

    send(text);
  }, [send]);

  /**
   * Give up on the answer that is coming.
   *
   * The reason this exists is not impatience. Every automatic way out of a
   * hung turn depends on noticing the hang, and the one failure mode nobody
   * can enumerate in advance is the one where nothing is noticed — so there
   * has to be a control that does not depend on detection at all. What is on
   * screen is kept and marked as a fragment; the composer comes back.
   */
  const stop = useCallback(() => {
    if (!pending) return;
    if (serviceRef.current) serviceRef.current.cancel();
    settleTurn(turnRef.current);
  }, [pending, settleTurn]);

  const reset = useCallback(() => {
    // Abort any in-flight stream before rotating the session id — otherwise
    // lingering events would land in the new conversation.
    if (serviceRef.current) serviceRef.current.cancel();
    const fresh = mintSessionId();
    saveSessionId(keys, fresh);
    // Drop the stored conversation snapshot too — a "new chat" should
    // come up empty even if the user immediately refreshes before sending
    // anything.
    clearStoredBlocks(keys);
    setSessionId(fresh);
    setBlocks([]);
    setError(null);
    setPending(false);
    setInterrupted(false);
    setStatus('connected');
    streamingIdRef.current = null;
    // Whatever was in the air belongs to the conversation just thrown away;
    // its settlement must not land on the new one.
    turnRef.current += 1;
    completedRef.current = true;
  }, [keys]);

  return {
    blocks,
    sessionId,
    status,
    error,
    pending,
    interrupted,
    send,
    answer,
    decideApproval,
    stop,
    reset,
  };
}
